import { Helmet } from "react-helmet-async";
import { Navigate, RouteObject } from "react-router-dom";
import AppLayout from "../layout/AppLayout";
import RouteGuard from "./RouteGuard";
import Dashboard from "../pages/Dashboard";
import NotFound from "../pages/404";
import Login from "../pages/Login";
import UsersList from "../pages/Users/UsersList";
import EditUser from "../pages/Users/EditUser";
import MotorPatternsList from "../pages/MotorPatterns/MotorPatternsList";
import MuscleGroupsList from "../pages/MuscleGroups/MuscleGroupsList";
import ExercisesList from "../pages/Exercises/ExercisesList";
import VariantsList from "../pages/Variants/VariantsList";
import TecnicheList from "../pages/TecnicheList/TecnicheList";
import TUTsList from "../pages/TUTs/TUTsList";
import RecsList from "../pages/RecsList/RecsList";
import RepSchemeTypesList from "../pages/RepSchemeTypes/RepSchemeTypesList";
import RepSchemesList from "../pages/RepSchemes/RepSchemesList";
import EffortTypesList from "../pages/EffortTypes/EffortTypesList";
import EffortsList from "../pages/Efforts/EffortsList";
import ProgrammazioniList from "../pages/Programmazioni/ProgrammazioniList";
import ProgrammazioneDetail from "../pages/Programmazioni/ProgrammazioneDetail";
import EditProgrammazione from "../pages/Programmazioni/EditProgrammazione";
import PrecompiledProgressionsList from "../pages/PrecompiledProgressions/PrecompiledProgressionsList";
import PrecompiledProgressionForm from "../pages/PrecompiledProgressions/PrecompiledProgressionForm";
import PrecompiledProgressionDetail from "../pages/PrecompiledProgressions/PrecompiledProgressionDetail";
import ProgrammazioniPubblicheList from "../pages/ProgrammazioniPubbliche/ProgrammazioniPubblicheList";
import VideoTutorialPage from "../pages/VideoTutorial/VideoTutorialPage";
import MentoringPage from "../pages/Mentoring/MentoringPage";
import VerifyUser from "../pages/VerifyUser";
import ReIssuePassword from "../pages/ReIssuePassword";
import AreaPersonale from "../pages/AreaPersonale/AreaPersonale";

type Props = {
  title: string;
  element: React.ReactNode;
};

function Page({ title, element }: Props) {
  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      {element}
    </>
  );
}

export const APP_ROUTES: Array<RouteObject> = [
  {
    path: "/verify/:token",
    element: <Page title="Verifica Account" element={<VerifyUser />} />,
  },
  {
    path: "/verify/re-issue-password/:token",
    element: <Page title="Reimposta Password" element={<ReIssuePassword />} />,
  },
  {
    path: "/",
    element: <AppLayout />,
    children: [
      {
        index: true,
        element: (
          <Navigate to={window.__env.AFTER_LOGIN_ROUTE || "dashboard"} />
        ),
      },
      {
        path: "/dashboard",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: <Page title="Dashboard" element={<Dashboard />} />,
          },
        ],
      },
      {
        path: "/area-personale",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: <Page title="Area Personale" element={<AreaPersonale />} />,
          },
        ],
      },
      {
        path: "/utenti",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: <Page title="Utenti" element={<UsersList />} />,
          },
          {
            path: "nuovo",
            element: <EditUser />,
          },
          {
            path: ":id",
            element: <EditUser />,
          },
        ],
      },
      {
        path: "/pattern-motori",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: (
              <Page title="Pattern Motori" element={<MotorPatternsList />} />
            ),
          },
        ],
      },
      {
        path: "/gruppi-muscolari",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: (
              <Page title="Gruppi Muscolari" element={<MuscleGroupsList />} />
            ),
          },
        ],
      },
      {
        path: "/progressioni-precompilate",
        element: <RouteGuard checkAdmin />,
        children: [
          {
            index: true,
            element: (
              <Page title="Progressioni Precompilate" element={<PrecompiledProgressionsList />} />
            ),
          },
          {
            path: "nuovo",
            element: <PrecompiledProgressionForm />,
          },
          {
            path: ":id",
            element: <PrecompiledProgressionDetail />,
          },
          {
            path: ":id/modifica",
            element: <PrecompiledProgressionForm />,
          },
        ],
      },
      {
        path: "/programmazioni-pubbliche",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: (
              <Page title="Programmazioni Pubbliche" element={<ProgrammazioniPubblicheList />} />
            ),
          },
        ],
      },
      {
        path: "/mentoring",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: <Page title="Mentoring" element={<MentoringPage />} />,
          },
        ],
      },
      {
        path: "/video-tutorial",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: <Page title="Video Tutorial" element={<VideoTutorialPage />} />,
          },
        ],
      },
      {
        path: "/programmazioni",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: (
              <Page title="Programmazioni" element={<ProgrammazioniList />} />
            ),
          },
          {
            path: ":id",
            element: <ProgrammazioneDetail />,
          },
          {
            path: "create",
            element: <RouteGuard checkAdmin />,
            children: [
              { index: true, element: <EditProgrammazione /> },
            ],
          },
          {
            path: ":id/:action",
            element: <RouteGuard checkAdmin />,
            children: [
              { index: true, element: <EditProgrammazione /> },
            ],
          },
        ],
      },
      {
        path: "/esercizi",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: <Page title="Esercizi" element={<ExercisesList />} />,
          },
        ],
      },
      {
        path: "/varianti",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: <Page title="Varianti" element={<VariantsList />} />,
          },
        ],
      },
      {
        path: "/tecniche",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: <Page title="Tecniche" element={<TecnicheList />} />,
          },
        ],
      },
      {
        path: "/time-under-tension",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: <Page title="Time Under Tension" element={<TUTsList />} />,
          },
        ],
      },
      {
        path: "/recupero",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: <Page title="Tempi di Recupero" element={<RecsList />} />,
          },
        ],
      },
      {
        path: "/rep-scheme-type",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: (
              <Page title="Rep Scheme Type" element={<RepSchemeTypesList />} />
            ),
          },
        ],
      },
      {
        path: "/rep-scheme",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: <Page title="Rep Scheme" element={<RepSchemesList />} />,
          },
        ],
      },
      {
        path: "/effort-type",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: <Page title="Effort Type" element={<EffortTypesList />} />,
          },
        ],
      },
      {
        path: "/effort",
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: <Page title="Effort" element={<EffortsList />} />,
          },
        ],
      },
      {
        path: "*",
        element: <Page title="Pagina non trovata" element={<NotFound />} />,
      },
      {
        path: "/login",
        element: <Page title="Login" element={<Login />} />,
      },
    ],
  },
];
